import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isDesktopApp, stopDesktopProctoring, submitProctoringData, logCheatingEvent } from '../../utils/desktopBridge';
import screenRecordingManager from '../Questions/ScreenRecordingManager';
import requestQueue from '../Questions/RequestQueue';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading, Text } from '@/components/ui/typography';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import useVoiceAnswer from '../../hooks/useVoiceAnswer';
import ProctoringEngine from '../Coding/Proctoring/ProctoringEngine';
import LockdownEngine from '../Coding/Lockdown/LockdownEngine';
import SnapshotEngine from '../Coding/Snapshots/SnapshotEngine';
import {
  CheckCircle2,
  Clock,
  Circle,
  Bookmark,
  BookmarkCheck,
  List,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  SkipForward,
  X,
  CheckCircle,
  Mic,
  MicOff,
  PenLine,
  Volume2,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MAX_VIOLATIONS = 3;

export default function AptitudeTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const test_id = searchParams.get('test_id');

  const passedQuestions = location.state?.questions || [];

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(30 * 60);
  const timerRef = useRef(null);

  // answers keyed by question id: MCQ = option index (number), voice = transcript (string)
  const [answers, setAnswers] = useState({});
  const [statuses, setStatuses] = useState({}); // id -> 'not-visited'|'answered'|'skipped'|'marked'
  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeSection, setActiveSection] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [violationWarning, setViolationWarning] = useState(null);
  const [voiceAttemptsLeft, setVoiceAttemptsLeft] = useState({}); // id -> re-records remaining
  // Accessibility fallback: verbal questions answered by typing (no working mic).
  // Tagged in the submission so the recruiter sees HOW each answer was given.
  const [typedVerbal, setTypedVerbal] = useState({}); // id -> true

  const autoSaveKey = `apt_autosave_${test_id}`;
  const timeSpentRef = useRef({});          // question id -> seconds
  const questionEnteredAtRef = useRef(Date.now());
  const violationsRef = useRef({ tab: 0, fullscreen: 0 });
  const submittedRef = useRef(false);
  const answersRef = useRef({});
  const [webcamStream, setWebcamStream] = useState(null);
  // Screen recording started in TestingSection continues through this page;
  // we adopt its recorder so submit can stop it and trigger the S3 merge —
  // without this, {test_id}_screen_recording.webm is never assembled and the
  // recruiter's "screen recording" link falls back to an OLDER attempt's file.
  const screenRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  // Camera self-view: candidates must always SEE that they're being recorded.
  // The thumbnail can be minimized to a REC pill but never fully hidden.
  const [previewMinimized, setPreviewMinimized] = useState(false);
  const previewVideoRef = useRef(null);

  const { networkStatus, networkSpeed, NetworkIcon } = useNetworkStatus();
  const voice = useVoiceAnswer();

  useEffect(() => { answersRef.current = answers; }, [answers]);

  // ── Load questions ─────────────────────────────────────────────────────────
  useEffect(() => {
    const init = (qs, durationMinutes) => {
      setQuestions(qs);
      setTimeLeft((durationMinutes || 30) * 60);
      setActiveSection(qs[0]?.section || null);
      const attempts = {};
      qs.forEach((q) => { if (q.type === 'voice') attempts[q.id] = 1; });
      setVoiceAttemptsLeft(attempts);
      // Restore autosave (crash / reload recovery)
      try {
        const saved = JSON.parse(localStorage.getItem(autoSaveKey) || 'null');
        if (saved && saved.test_id === test_id) {
          setAnswers(saved.answers || {});
          setStatuses(saved.statuses || {});
          setTypedVerbal(saved.typedVerbal || {});
          if (typeof saved.currentIdx === 'number') setCurrentIdx(Math.min(saved.currentIdx, qs.length - 1));
          if (typeof saved.timeLeft === 'number') setTimeLeft(saved.timeLeft);
          if (saved.timeSpent) timeSpentRef.current = saved.timeSpent;
        }
      } catch (e) { /* corrupt autosave — start fresh */ }
      setLoading(false);
    };

    if (passedQuestions.length > 0) {
      init(passedQuestions, location.state?.durationMinutes);
    } else {
      (async () => {
        try {
          const resp = await fetch(`/api/get_questions?test_id=${encodeURIComponent(test_id)}`);
          if (!resp.ok) throw new Error(`Status ${resp.status}`);
          const data = await resp.json();
          init(data.question_data || [], data.duration_minutes);
        } catch (err) {
          console.error('Failed to fetch aptitude questions', err);
          setError('Failed to load your test. Please refresh to retry.');
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Countdown + autosave ───────────────────────────────────────────────────
  useEffect(() => {
    if (loading || submitted) return undefined;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, submitted]);

  useEffect(() => {
    if (loading) return undefined;
    const id = setInterval(() => {
      try {
        localStorage.setItem(autoSaveKey, JSON.stringify({
          test_id, answers, statuses, currentIdx, timeLeft, typedVerbal, timeSpent: timeSpentRef.current,
        }));
      } catch (e) { /* storage full — non-fatal */ }
    }, 4000);
    return () => clearInterval(id);
  }, [answers, statuses, currentIdx, timeLeft, typedVerbal, loading, autoSaveKey, test_id]);

  // ── Adopt the screen recording started in TestingSection ─────────────────
  useEffect(() => {
    const s = screenRecordingManager.getScreenRecordingState();
    if (s.isScreenRecording && s.screenRecorderRef) {
      screenRecorderRef.current = s.screenRecorderRef;
      screenStreamRef.current = s.screenStreamRef;
    }
    return () => screenRecordingManager.clearScreenRecordingState();
  }, []);

  const stopScreenRecording = async () => {
    const recorder = screenRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    await new Promise((resolve) => {
      const originalOnStop = recorder.onstop;
      recorder.onstop = async () => {
        if (originalOnStop) {
          try { await originalOnStop(); } catch (e) { /* flush best-effort */ }
        }
        resolve();
      };
      try { recorder.stop(); } catch (err) { resolve(); }
    });
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* ignore */ } });
    }
    screenRecorderRef.current = null;
    screenStreamRef.current = null;
  };

  // ── Webcam for the proctoring engines ─────────────────────────────────────
  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: true })
      .then((s) => { stream = s; setWebcamStream(s); })
      .catch((e) => console.warn('[Aptitude] webcam unavailable for proctoring', e));
    return () => { stream?.getTracks().forEach((t) => t.stop()); };
  }, []);

  // Attach the webcam stream to the self-view. Re-runs when the thumbnail is
  // expanded again, because minimizing unmounts the <video> element.
  useEffect(() => {
    if (previewVideoRef.current && webcamStream) {
      previewVideoRef.current.srcObject = webcamStream;
      previewVideoRef.current.play().catch(() => {});
    }
  }, [webcamStream, previewMinimized]);

  // ── Violations: tab switch + fullscreen exit, 3 strikes → zero submission ──
  const handleViolation = useCallback((kind) => {
    if (submittedRef.current) return;
    violationsRef.current[kind] += 1;
    const count = violationsRef.current[kind];
    logCheatingEvent?.(kind === 'tab' ? 'tab_switch' : 'fullscreen_exit', 'high', {
      count, test_id, timestamp: new Date().toISOString(),
    });
    if (count >= MAX_VIOLATIONS) {
      setViolationWarning(null);
      forceZeroSubmit();
    } else {
      setViolationWarning({
        kind,
        remaining: MAX_VIOLATIONS - count,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test_id]);

  useEffect(() => {
    if (loading || submitted) return undefined;
    const onVisibility = () => { if (document.hidden) handleViolation('tab'); };
    const onFullscreen = () => {
      const inFs = document.fullscreenElement || document.webkitFullscreenElement;
      if (!inFs) handleViolation('fullscreen');
    };
    const blockContext = (e) => e.preventDefault();
    const blockKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'p', 'u'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
      if (e.key === 'F12') e.preventDefault();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('fullscreenchange', onFullscreen);
    document.addEventListener('webkitfullscreenchange', onFullscreen);
    document.addEventListener('contextmenu', blockContext);
    document.addEventListener('keydown', blockKeys);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', onFullscreen);
      document.removeEventListener('webkitfullscreenchange', onFullscreen);
      document.removeEventListener('contextmenu', blockContext);
      document.removeEventListener('keydown', blockKeys);
    };
  }, [loading, submitted, handleViolation]);

  // ── Sections ───────────────────────────────────────────────────────────────
  const sections = useMemo(() => {
    const seen = new Map();
    questions.forEach((q) => {
      if (!seen.has(q.section)) seen.set(q.section, q.section_label || q.section);
    });
    return Array.from(seen, ([key, label]) => ({ key, label }));
  }, [questions]);

  const sectionQuestionIdxs = useMemo(() => {
    const map = {};
    questions.forEach((q, i) => {
      (map[q.section] = map[q.section] || []).push(i);
    });
    return map;
  }, [questions]);

  const recordTimeSpent = useCallback((idx) => {
    const q = questions[idx];
    if (!q) return;
    const secs = Math.round((Date.now() - questionEnteredAtRef.current) / 1000);
    timeSpentRef.current[q.id] = (timeSpentRef.current[q.id] || 0) + secs;
    questionEnteredAtRef.current = Date.now();
  }, [questions]);

  const jumpTo = useCallback((i) => {
    if (i < 0 || i >= questions.length) return;
    recordTimeSpent(currentIdx);
    if (voice.isRecording) voice.stopAnswer();
    setCurrentIdx(i);
    setActiveSection(questions[i].section);
    voice.resetAnswer();
  }, [currentIdx, questions, recordTimeSpent, voice]);

  const goNext = () => jumpTo(Math.min(questions.length - 1, currentIdx + 1));
  const goPrev = () => jumpTo(Math.max(0, currentIdx - 1));

  const setStatus = (id, status) => setStatuses((prev) => ({ ...prev, [id]: status }));

  const selectOption = (q, optIdx) => {
    setAnswers((prev) => ({ ...prev, [q.id]: optIdx }));
    setStatus(q.id, 'answered');
  };

  const setWrittenAnswer = (q, text) => {
    setAnswers((prev) => ({ ...prev, [q.id]: text }));
    if (text.trim()) setStatus(q.id, 'answered');
  };

  const skipQuestion = () => {
    const q = questions[currentIdx];
    if (q && statuses[q.id] !== 'answered') setStatus(q.id, 'skipped');
    goNext();
  };

  const toggleMark = () => {
    const q = questions[currentIdx];
    if (!q) return;
    const current = statuses[q.id];
    if (current === 'marked') {
      setStatus(q.id, answers[q.id] !== undefined && answers[q.id] !== '' ? 'answered' : 'skipped');
    } else {
      setStatus(q.id, 'marked');
    }
  };

  // ── Voice answer flow ──────────────────────────────────────────────────────
  const handleStartVoiceAnswer = async () => {
    const q = questions[currentIdx];
    if (!q) return;
    const ok = await voice.startAnswer();
    if (!ok) return;
    // A fresh recording replaces any previous transcript for this question
    setAnswers((prev) => ({ ...prev, [q.id]: '' }));
  };

  const handleStopVoiceAnswer = async () => {
    const q = questions[currentIdx];
    if (!q) return;
    const transcript = await voice.stopAnswer();
    setAnswers((prev) => ({ ...prev, [q.id]: transcript }));
    setStatus(q.id, transcript ? 'answered' : 'skipped');
  };

  const switchVerbalToTyped = async (q) => {
    if (voice.isRecording) await voice.stopAnswer();
    voice.resetAnswer();
    setTypedVerbal((prev) => ({ ...prev, [q.id]: true }));
    setAnswers((prev) => ({ ...prev, [q.id]: '' }));
    setStatus(q.id, 'not-visited');
  };

  const switchVerbalToSpoken = (q) => {
    setTypedVerbal((prev) => { const c = { ...prev }; delete c[q.id]; return c; });
    setAnswers((prev) => ({ ...prev, [q.id]: '' }));
    setStatus(q.id, 'not-visited');
    voice.resetAnswer();
  };

  const handleReRecord = () => {
    const q = questions[currentIdx];
    if (!q || (voiceAttemptsLeft[q.id] || 0) <= 0) return;
    setVoiceAttemptsLeft((prev) => ({ ...prev, [q.id]: prev[q.id] - 1 }));
    setAnswers((prev) => ({ ...prev, [q.id]: '' }));
    setStatus(q.id, 'not-visited');
    voice.resetAnswer();
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const doSubmit = async (auto, forceZero) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      if (voice.isRecording) {
        const q = questions[currentIdx];
        const transcript = await voice.stopAnswer();
        if (q?.type === 'voice' && transcript) {
          answersRef.current = { ...answersRef.current, [q.id]: transcript };
        }
      }
      recordTimeSpent(currentIdx);

      if (isDesktopApp()) {
        try {
          await stopDesktopProctoring();
          await submitProctoringData(test_id, 'apt');
        } catch (e) {
          console.error('[Desktop] proctoring stop failed', e);
        }
      }

      const payload = {
        test_id,
        answers: answersRef.current,
        time_per_question: timeSpentRef.current,
        answer_modes: Object.fromEntries(
          Object.keys(typedVerbal)
            .filter((id) => typedVerbal[id])
            .map((id) => [id, 'typed']),
        ),
        meta: {
          time_left: timeLeft,
          auto_submitted: !!auto,
          force_zero: !!forceZero,
          violations: violationsRef.current,
        },
      };
      const res = await axios.post('/api/submit_aptitude_answers', payload);
      if (res.status === 200) {
        // Stop the screen recorder (flushes the final chunk) then ask the
        // server to merge this attempt's chunks into
        // {test_id}_screen_recording.webm — same contract as the coding test.
        try {
          await stopScreenRecording();
          // Screen chunks upload through the low-priority queue — drain it so
          // every chunk reaches the server BEFORE the merge assembles the file
          // (skipping this is exactly how recordings ended up missing).
          await Promise.race([
            requestQueue.drainLowPriorityQueue(),
            new Promise((r) => setTimeout(r, 20000)),
          ]);
          fetch('/api/merge_screen_recording', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ test_id }),
          });
        } catch (e) {
          console.warn('[Aptitude] screen recording finalize failed', e);
        }
        setSubmitted(true);
        localStorage.removeItem(autoSaveKey);
      } else {
        submittedRef.current = false;
        setError('Submission failed — please try again.');
      }
    } catch (err) {
      console.error(err);
      submittedRef.current = false;
      setError('Submission failed — please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (auto = false) => doSubmit(auto, false);
  const forceZeroSubmit = () => doSubmit(true, true);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const statusIcon = (status, isActive) => {
    const base = 'w-4 h-4 shrink-0';
    switch (status) {
      case 'answered': return <CheckCircle className={cn(base, isActive ? 'text-primary-foreground' : 'text-success')} />;
      case 'skipped': return <Circle className={cn(base, isActive ? 'text-primary-foreground' : 'text-destructive')} />;
      case 'marked': return <BookmarkCheck className={cn(base, isActive ? 'text-primary-foreground' : 'text-primary')} />;
      default: return <Circle className={cn(base, isActive ? 'text-primary-foreground/60' : 'text-muted-foreground/40')} />;
    }
  };

  if (loading) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center')}>
        <Text>Preparing your aptitude test…</Text>
      </div>
    );
  }
  if (error && questions.length === 0) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center')}>
        <Text className={cn('text-destructive')}>{error}</Text>
      </div>
    );
  }

  const q = questions[currentIdx] || {};
  const answeredCount = questions.filter((qq) => {
    const a = answers[qq.id];
    return a !== undefined && a !== null && a !== '';
  }).length;
  const sectionIdxs = sectionQuestionIdxs[activeSection] || [];

  return (
    <div className={cn('min-h-screen flex flex-col bg-background')}>
      {/* Camera-based proctoring layers — offscreen video + overlays only */}
      <ProctoringEngine stream={webcamStream} testId={test_id} active={!submitted} />
      <LockdownEngine testId={test_id} active={!submitted} />
      <SnapshotEngine stream={webcamStream} testId={test_id} active={!submitted} />

      {/* ===== HEADER ===== */}
      <header className={cn('flex items-center justify-between px-6 py-4 backdrop-blur-sm border-b border-border min-h-[64px] flex-shrink-0 bg-card/50')}>
        <div className={cn('flex items-center gap-4')}>
          <div className={cn('text-lg font-bold text-primary uppercase tracking-wide')}>AI Evaluator</div>
          <div className={cn('h-6 w-px bg-border')} />
          <Badge variant="secondary" className={cn('text-xs')}>
            Aptitude Test · {answeredCount}/{questions.length} answered
          </Badge>
        </div>

        <div className={cn('flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-md border border-border')}>
          <Clock className={cn('w-4 h-4', timeLeft <= 60 ? 'text-destructive' : timeLeft <= 300 ? 'text-warning' : 'text-foreground')} />
          <span className={cn('font-mono font-semibold text-sm tabular-nums', timeLeft <= 60 ? 'text-destructive' : timeLeft <= 300 ? 'text-warning' : 'text-foreground')}>
            {formatTime(timeLeft)}
          </span>
        </div>

        <div className={cn('flex items-center gap-3')}>
          <div className={cn('flex items-center gap-2')}>
            <NetworkIcon className={cn('w-4 h-4', networkStatus === 'good' ? 'text-success' : networkStatus === 'fair' ? 'text-warning' : 'text-destructive')} />
            <span className={cn('text-muted-foreground text-xs')}>
              {networkSpeed ? `${parseFloat(networkSpeed).toFixed(1)} Mbps` : '—'}
            </span>
          </div>
          <div className={cn('h-6 w-px bg-border')} />
          <Button
            onClick={() => handleSubmit(false)}
            disabled={submitted || submitting}
            className={cn('h-10 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold px-4')}
          >
            <CheckCircle2 className={cn('w-4 h-4 mr-1.5')} />
            {submitting ? 'Submitting…' : 'Submit Test'}
          </Button>
        </div>
      </header>

      {/* ===== SECTION TABS ===== */}
      <div className={cn('flex items-center gap-2 px-6 py-3 border-b border-border bg-card/30 overflow-x-auto')}>
        {sections.map((s) => {
          const idxs = sectionQuestionIdxs[s.key] || [];
          const done = idxs.filter((i) => {
            const a = answers[questions[i].id];
            return a !== undefined && a !== null && a !== '';
          }).length;
          const isActive = s.key === activeSection;
          return (
            <button
              key={s.key}
              onClick={() => jumpTo(idxs[0] ?? 0)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors border',
                isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground border-border hover:bg-accent',
              )}
            >
              {s.label}
              <span className={cn('ml-2 text-xs', isActive ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                {done}/{idxs.length}
              </span>
            </button>
          );
        })}
      </div>

      {/* ===== MAIN ===== */}
      <div className={cn('flex-1 flex overflow-hidden')}>
        <aside className={cn('w-64 border-r border-border bg-card p-5 flex flex-col gap-4 overflow-y-auto')}>
          <Text className={cn('text-xs font-semibold text-muted-foreground uppercase tracking-wide')}>
            {sections.find((s) => s.key === activeSection)?.label} ({sectionIdxs.length})
          </Text>
          <div className={cn('grid grid-cols-4 gap-2')}>
            {sectionIdxs.map((i) => {
              const qq = questions[i];
              const isActive = i === currentIdx;
              const status = statuses[qq.id] || 'not-visited';
              return (
                <button
                  key={qq.id}
                  onClick={() => jumpTo(i)}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 rounded-md border py-2 text-xs font-medium transition-colors',
                    isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-accent',
                  )}
                  title={`Question ${i + 1}`}
                >
                  {statusIcon(status, isActive)}
                  {i + 1}
                </button>
              );
            })}
          </div>
          <Separator />
          <div className={cn('space-y-1.5 text-xs text-muted-foreground')}>
            <div className={cn('flex items-center gap-2')}><CheckCircle className={cn('w-3.5 h-3.5 text-success')} /> Answered</div>
            <div className={cn('flex items-center gap-2')}><Circle className={cn('w-3.5 h-3.5 text-destructive')} /> Skipped</div>
            <div className={cn('flex items-center gap-2')}><BookmarkCheck className={cn('w-3.5 h-3.5 text-primary')} /> Marked for review</div>
            <div className={cn('flex items-center gap-2')}><Mic className={cn('w-3.5 h-3.5')} /> Verbal questions are answered by speaking</div>
          </div>
        </aside>

        <main className={cn('flex-1 p-8 overflow-y-auto')}>
          <div className={cn('mb-6 flex items-center gap-3')}>
            <Badge variant="outline" className={cn('text-xs')}>
              Question {currentIdx + 1} of {questions.length}
            </Badge>
            <Badge variant="outline" className={cn('text-xs')}>
              {q.type === 'mcq' ? (<><List className={cn('w-3 h-3 mr-1.5')} />Multiple Choice</>)
                : q.type === 'written' ? (<><PenLine className={cn('w-3 h-3 mr-1.5')} />Written Answer</>)
                : (<><Mic className={cn('w-3 h-3 mr-1.5')} />Verbal Answer</>)}
            </Badge>
            <Badge variant="secondary" className={cn('text-xs')}>{q.section_label}</Badge>
          </div>

          <Card className={cn('shadow-sm')}>
            <CardContent className={cn('p-8')}>
              <Heading level="h2" className={cn('text-xl font-semibold mb-8 text-foreground leading-relaxed whitespace-pre-wrap')}>
                {q.question}
              </Heading>

              {q.type === 'mcq' && (
                <RadioGroup
                  value={answers[q.id] !== undefined && answers[q.id] !== null ? String(answers[q.id]) : ''}
                  onValueChange={(value) => selectOption(q, Number(value))}
                  className={cn('space-y-3')}
                >
                  {(q.options || []).map((opt, idx) => {
                    const isSelected = answers[q.id] === idx;
                    return (
                      <div
                        key={idx}
                        className={cn(
                          'flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer',
                          isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-primary/50 hover:bg-accent/30',
                        )}
                        onClick={() => selectOption(q, idx)}
                      >
                        <RadioGroupItem value={String(idx)} id={`opt-${q.id}-${idx}`} className={cn('mt-0.5')} />
                        <Label htmlFor={`opt-${q.id}-${idx}`} className={cn('flex-1 cursor-pointer text-foreground leading-relaxed')}>
                          {opt}
                        </Label>
                        {isSelected && <CheckCircle className={cn('w-5 h-5 text-primary shrink-0')} />}
                      </div>
                    );
                  })}
                </RadioGroup>
              )}

              {q.type === 'written' && (
                <div className={cn('space-y-2')}>
                  <Label className={cn('text-sm font-medium')}>Type your answer</Label>
                  <Textarea
                    value={typeof answers[q.id] === 'string' ? answers[q.id] : ''}
                    onChange={(e) => setWrittenAnswer(q, e.target.value)}
                    placeholder="Write your answer here — include your reasoning, not just the result…"
                    className={cn('min-h-[180px] text-sm leading-relaxed')}
                  />
                  <Text className={cn('text-xs text-muted-foreground flex items-center gap-1.5')}>
                    <CheckCircle2 className={cn('w-3.5 h-3.5')} />
                    Saved automatically as you type
                  </Text>
                </div>
              )}

              {q.type === 'voice' && !typedVerbal[q.id] && (
                <div className={cn('space-y-5')}>
                  <div className={cn('flex items-center gap-3')}>
                    <Button variant="outline" onClick={() => voice.speakQuestion(q.question)} disabled={voice.isSpeaking || voice.isRecording} className={cn('gap-2')}>
                      <Volume2 className={cn('w-4 h-4')} />
                      {voice.isSpeaking ? 'Playing…' : 'Read question aloud'}
                    </Button>
                    {!voice.isRecording ? (
                      <Button
                        onClick={handleStartVoiceAnswer}
                        disabled={voice.isSpeaking || (!!answers[q.id] && (voiceAttemptsLeft[q.id] || 0) <= 0)}
                        className={cn('gap-2 bg-primary text-primary-foreground')}
                      >
                        <Mic className={cn('w-4 h-4')} />
                        {answers[q.id] ? 'Record again' : 'Start answering'}
                      </Button>
                    ) : (
                      <Button onClick={handleStopVoiceAnswer} variant="destructive" className={cn('gap-2')}>
                        <MicOff className={cn('w-4 h-4')} />
                        Done — stop recording
                      </Button>
                    )}
                  </div>

                  {voice.isRecording && (
                    <div className={cn('p-4 rounded-lg border-2 border-primary/60 bg-primary/5')}>
                      <div className={cn('flex items-center gap-2 mb-2')}>
                        <span className={cn('relative flex h-3 w-3')}>
                          <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75')} />
                          <span className={cn('relative inline-flex rounded-full h-3 w-3 bg-destructive')} />
                        </span>
                        <Text className={cn('text-sm font-medium')}>Listening… speak your answer (auto-stops after {voice.maxAnswerSeconds}s)</Text>
                      </div>
                      <Text className={cn('text-sm text-muted-foreground min-h-[24px]')}>
                        {voice.liveTranscript || voice.finalTranscript || '…'}
                      </Text>
                    </div>
                  )}

                  {!voice.isRecording && answers[q.id] && (
                    <div className={cn('p-4 rounded-lg border border-border bg-muted/30 space-y-3')}>
                      <Text className={cn('text-xs font-semibold text-muted-foreground uppercase tracking-wide')}>Your recorded answer</Text>
                      <Text className={cn('text-sm text-foreground leading-relaxed')}>{answers[q.id]}</Text>
                      {(voiceAttemptsLeft[q.id] || 0) > 0 && (
                        <Button variant="outline" size="sm" onClick={handleReRecord} className={cn('gap-2')}>
                          <RotateCcw className={cn('w-3.5 h-3.5')} />
                          Re-record ({voiceAttemptsLeft[q.id]} attempt left)
                        </Button>
                      )}
                    </div>
                  )}

                  {voice.voiceError && (
                    <Text className={cn('text-sm text-destructive flex items-center gap-2')}>
                      <AlertTriangle className={cn('w-4 h-4')} /> {voice.voiceError}
                    </Text>
                  )}

                  {/* Accessibility fallback — quiet secondary option, always available */}
                  {!voice.isRecording && (
                    <button
                      type="button"
                      onClick={() => switchVerbalToTyped(q)}
                      className={cn('text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground')}
                    >
                      Can't use a microphone? Type your answer instead
                    </button>
                  )}
                </div>
              )}

              {q.type === 'voice' && typedVerbal[q.id] && (
                <div className={cn('space-y-2')}>
                  <div className={cn('flex items-center justify-between gap-2 flex-wrap')}>
                    <Label className={cn('text-sm font-medium flex items-center gap-1.5')}>
                      <PenLine className={cn('w-4 h-4')} /> Typed answer (microphone fallback)
                    </Label>
                    <button
                      type="button"
                      onClick={() => switchVerbalToSpoken(q)}
                      className={cn('text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground')}
                    >
                      Switch back to speaking
                    </button>
                  </div>
                  <Textarea
                    value={typeof answers[q.id] === 'string' ? answers[q.id] : ''}
                    onChange={(e) => setWrittenAnswer(q, e.target.value)}
                    placeholder="Type what you would have said — your result and your reasoning…"
                    className={cn('min-h-[160px] text-sm leading-relaxed')}
                  />
                </div>
              )}

              <Separator className={cn('my-6')} />

              <div className={cn('flex justify-between items-center')}>
                <div className={cn('flex gap-2')}>
                  <Button variant="outline" onClick={skipQuestion} className={cn('gap-2')}>
                    <SkipForward className={cn('w-4 h-4')} />
                    Skip
                  </Button>
                  <Button variant="outline" onClick={goPrev} disabled={currentIdx === 0} className={cn('gap-2')}>
                    <ChevronLeft className={cn('w-4 h-4')} />
                    Previous
                  </Button>
                </div>
                <div className={cn('flex gap-2')}>
                  <Button variant={statuses[q.id] === 'marked' ? 'default' : 'outline'} onClick={toggleMark} className={cn('gap-2')}>
                    {statuses[q.id] === 'marked' ? (<><BookmarkCheck className={cn('w-4 h-4')} />Unmark</>) : (<><Bookmark className={cn('w-4 h-4')} />Mark for Review</>)}
                  </Button>
                  <Button onClick={goNext} disabled={currentIdx === questions.length - 1} className={cn('gap-2')}>
                    Next
                    <ChevronRight className={cn('w-4 h-4')} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {error && (
            <Text className={cn('mt-4 text-sm text-destructive flex items-center gap-2')}>
              <AlertTriangle className={cn('w-4 h-4')} /> {error}
            </Text>
          )}
        </main>
      </div>

      {/* ===== CAMERA SELF-VIEW ===== always-visible recording indicator;
           thumbnail is minimizable to a REC pill, never fully removable */}
      {webcamStream && !submitted && (
        previewMinimized ? (
          <button
            onClick={() => setPreviewMinimized(false)}
            className={cn('fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full bg-zinc-900/90 border border-zinc-700 px-3 py-1.5 shadow-lg hover:bg-zinc-800')}
            title="Show camera preview"
          >
            <span className={cn('relative flex h-2 w-2')}>
              <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75')} />
              <span className={cn('relative inline-flex rounded-full h-2 w-2 bg-red-500')} />
            </span>
            <span className={cn('text-[11px] font-semibold text-red-300 tracking-wide')}>REC</span>
            <ChevronUp className={cn('w-3.5 h-3.5 text-zinc-400')} />
          </button>
        ) : (
          <div className={cn('fixed bottom-4 left-4 z-40 w-[200px] rounded-lg overflow-hidden border border-zinc-700 bg-zinc-900 shadow-lg')}>
            <video
              ref={previewVideoRef}
              muted
              playsInline
              autoPlay
              className={cn('w-full h-[128px] object-cover')}
              style={{ transform: 'scaleX(-1)' }}
            />
            <div className={cn('absolute top-1.5 left-2 flex items-center gap-1')}>
              <span className={cn('relative flex h-2 w-2')}>
                <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75')} />
                <span className={cn('relative inline-flex rounded-full h-2 w-2 bg-red-500')} />
              </span>
              <span className={cn('text-[10px] font-semibold text-red-300 tracking-wide')}>REC</span>
            </div>
            <button
              onClick={() => setPreviewMinimized(true)}
              className={cn('absolute top-1 right-1 rounded p-0.5 text-zinc-300 hover:bg-zinc-700/70')}
              title="Minimize camera preview (recording continues)"
            >
              <ChevronDown className={cn('w-4 h-4')} />
            </button>
          </div>
        )
      )}

      {/* Violation warning */}
      <Dialog open={!!violationWarning} onOpenChange={() => setViolationWarning(null)}>
        <DialogContent className={cn('sm:max-w-[425px]')}>
          <DialogHeader>
            <div className={cn('flex justify-center mb-3')}>
              <div className={cn('w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center')}>
                <AlertTriangle className={cn('w-8 h-8 text-destructive')} />
              </div>
            </div>
            <DialogTitle className={cn('text-center')}>
              {violationWarning?.kind === 'tab' ? 'Tab switch detected' : 'Fullscreen exited'}
            </DialogTitle>
            <DialogDescription className={cn('text-center')}>
              Leaving the test window is a proctoring violation. {violationWarning?.remaining} warning
              {violationWarning?.remaining === 1 ? '' : 's'} remaining — after that the test is submitted with zero marks.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn('flex justify-center')}>
            <Button onClick={() => setViolationWarning(null)}>I understand</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <Dialog open={submitted} onOpenChange={() => {}}>
        <DialogContent className={cn('sm:max-w-[425px] text-center')}>
          <DialogHeader>
            <div className={cn('flex justify-center mb-4')}>
              <div className={cn('w-16 h-16 rounded-full bg-success/10 flex items-center justify-center')}>
                <CheckCircle2 className={cn('w-10 h-10 text-success')} />
              </div>
            </div>
            <DialogTitle className={cn('text-2xl font-bold text-foreground')}>Test Submitted Successfully</DialogTitle>
            <DialogDescription className={cn('text-muted-foreground mt-2')}>
              Your aptitude test has been submitted. Thank you for completing the assessment!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn('flex justify-center mt-6')}>
            <Button onClick={() => navigate('/user-home', { state: { showTestSubmittedPopup: true } })} className={cn('gap-2')}>
              <CheckCircle className={cn('w-4 h-4')} />
              Return to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
